import React, { useState } from "react";
import { Grid } from '@material-ui/core';
import {
  Box, Card, CardContent, FormControl, InputLabel, Alert, AlertTitle,
  Button, Select, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper
} from '@mui/material';
import MainCard from '../components/MainCard';
import axios from "../../components/axios";
import { useNavigate } from 'react-router-dom';

const styleButton = {
  backgroundColor: "#406599",
  color: "#fff",
  borderRadius: "5px",
  boxSizing: "border-box",
  width: "100px",
  height: "45px",
  fontFamily: '"Roboto", "sans-serif"',
  fontSize: "15px",
};

const styleExportButton = {
  backgroundColor: "#2e7d32",
  color: "#fff",
  borderRadius: "5px",
  boxSizing: "border-box",
  height: "36px",
  fontFamily: '"Roboto", "sans-serif"',
  fontSize: "14px",
  padding: "0 16px",
};

const columns = [
  { id: 'project',          label: 'Project' },
  { id: 'network_type',     label: 'Network Type' },
  { id: 'build_number',     label: 'Build Number' },
  { id: 'build_type',       label: 'Build Type' },
  { id: 'test_suite',       label: 'Test Suite' },
  { id: 'device_sn',        label: 'Device SN' },
  { id: 'data',             label: 'Data' },
  { id: 'zygote_lunch_time',label: 'Zygote Lunch Time' },
  { id: 'memo',             label: 'Memo' },
  { id: 'is_2g',            label: 'is_2G' },
  { id: 'finish_on',        label: 'Finish On' },
];

const projectOptions = ['Amber', 'Redstone'];

const PerformanceRawDataPage = () => {
  const navigate = useNavigate();
  const [project, setProject] = useState('');
  const [rows, setRows] = useState([]);
  const [showTable, setShowTable] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSearch = () => {
    if (!project) {
      alert("Please select a project!");
      return;
    }
    setRows([]);
    setShowTable(false);
    setShowAlert(false);
    setLoading(true);

    axios.post(
      "/peloton/v1/automation/performance-raw-data",
      JSON.stringify({ project }),
      { withCredentials: true }
    )
      .then(res => {
        setLoading(false);
        if (res.data.message === "success" && res.data.data && res.data.data.length > 0) {
          setRows(res.data.data);
          setShowTable(true);
        } else {
          setAlertMessage(res.data.message || 'No data found.');
          setShowAlert(true);
        }
      })
      .catch(err => {
        setLoading(false);
        if (!err.response) {
          alert(err.toString());
        } else if (err.response.status === 401) {
          navigate("/");
        } else {
          alert(err.response.data?.message || "Error");
        }
      });
  };

  const handleExportCSV = () => {
    if (rows.length === 0) return;

    const header = columns.map(c => c.label).join(',');
    const csvRows = rows.map(row =>
      columns.map(c => {
        const val = row[c.id] ?? '';
        const str = String(val);
        return str.includes(',') || str.includes('"') || str.includes('\n')
          ? `"${str.replace(/"/g, '""')}"`
          : str;
      }).join(',')
    );
    const csvContent = [header, ...csvRows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `performance_raw_data_${project}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <MainCard title="Automation Reports">
      <Card variant="outlined" sx={{ bgcolor: "white" }}>
        <CardContent>
          <Box sx={{ fontSize: '20px', color: "#406599", pb: '15px' }}>
            Performance Raw Data
          </Box>
          <Grid container direction="row" alignItems="center" spacing={2}>
            <Grid item xs={2}>
              <FormControl fullWidth>
                <InputLabel>Select Project</InputLabel>
                <Select
                  value={project}
                  onChange={e => setProject(e.target.value)}
                  variant="filled"
                >
                  {projectOptions.map(p => (
                    <MenuItem key={p} value={p}>{p}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item>
              <Button style={styleButton} onClick={handleSearch} disabled={loading}>
                {loading ? "Loading..." : "Search"}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {showTable && (
        <Card variant="outlined" sx={{ bgcolor: "white", mt: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Box sx={{ fontSize: '14px', color: '#555' }}>
                {rows.length} records
              </Box>
              <Button style={styleExportButton} onClick={handleExportCSV}>
                Export CSV
              </Button>
            </Box>
            <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    {columns.map(col => (
                      <TableCell
                        key={col.id}
                        sx={{ backgroundColor: '#406599', color: '#fff', whiteSpace: 'nowrap', fontWeight: 'bold' }}
                      >
                        {col.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row, idx) => (
                    <TableRow key={idx} hover>
                      {columns.map(col => (
                        <TableCell key={col.id} sx={{ whiteSpace: 'nowrap' }}>
                          {row[col.id] ?? ''}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {showAlert && (
        <Card variant="outlined" sx={{ bgcolor: "white", mt: 2 }}>
          <Alert severity="warning">
            <AlertTitle>Warning</AlertTitle>
            {alertMessage}
          </Alert>
        </Card>
      )}
    </MainCard>
  );
};

export default PerformanceRawDataPage;
